import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
    createImpersonationToken,
    getOrganizationBranches,
} from "services/organizationBranches/rest";
import {
    OrganizationBranchListDto,
    PaymentStatusDto,
} from "services/organizationBranches/restModels";
import { useDebouncedValue } from "shared/hooks/useDebouncedValue";
import { useNotificationSnackbar } from "shared/ui/NotificationSnackbar/useNotificationSnackbar";

import { PAGE_SIZE, PAYMENT_STATUS_LABELS, SEARCH_DEBOUNCE_MS, WAREHOUSE_URL } from "../constants";

// Разбор терпимый: правленый руками адрес не должен приводить к отказу бэка.
const readFilters = (search: string) => {
    const params = new URLSearchParams(search);
    const rawPaymentStatus = params.get("paymentStatus");
    const page = Number(params.get("page"));

    return {
        query: params.get("query") || "",
        branchId: (params.get("branchId") || "").replace(/\D/g, ""),
        paymentStatus:
            rawPaymentStatus && rawPaymentStatus in PAYMENT_STATUS_LABELS
                ? (rawPaymentStatus as PaymentStatusDto)
                : null,
        page: Number.isInteger(page) && page > 0 ? page : 1,
    };
};

export const useBranchesRegistryState = () => {
    const notify = useNotificationSnackbar();
    const history = useHistory();
    const location = useLocation();

    // Фильтры живут в адресе: иначе возврат с карточки заведения открывал бы список заново.
    const [initialFilters] = useState(() => readFilters(location.search));
    const [query, setQuery] = useState(initialFilters.query);
    const [branchId, setBranchId] = useState(initialFilters.branchId);
    const [paymentStatus, setPaymentStatus] = useState<PaymentStatusDto | null>(initialFilters.paymentStatus);
    const [page, setPage] = useState(initialFilters.page);

    const [items, setItems] = useState<OrganizationBranchListDto[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [busyBranchId, setBusyBranchId] = useState<number | null>(null);

    // Пустой запрос применяем сразу: иначе после сброса поиска экран ещё 300 мс живёт в
    // режиме поиска и успевает сходить за списком дважды.
    const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);
    const appliedQuery = query.trim() === "" ? "" : debouncedQuery;
    const debouncedBranchId = useDebouncedValue(branchId, SEARCH_DEBOUNCE_MS);
    const appliedBranchId = branchId.trim() === "" ? "" : debouncedBranchId;

    /*
    * Счётчик выборки: ответ по прошлому фильтру отбрасывается. Без него медленный ответ по
    * старому запросу дорисовывает свои строки поверх новых.
    * */
    const requestId = useRef(0);

    const loadBranches = useCallback(async () => {
        const currentRequest = requestId.current + 1;
        requestId.current = currentRequest;

        setLoading(true);

        const response = await getOrganizationBranches({
            organizationBranchId: appliedBranchId ? Number(appliedBranchId) : null,
            query: appliedQuery || null,
            paymentStatus,
            skip: (page - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
        });

        if (currentRequest !== requestId.current) return;

        if (!response) {
            setError(true);
            setLoading(false);
            return;
        }

        setItems(response.items);
        setTotal(response.total);
        setError(false);
        setLoading(false);
    }, [appliedBranchId, appliedQuery, paymentStatus, page]);

    useEffect(() => {
        loadBranches();
    }, [loadBranches]);

    // Через replace: иначе «назад» браузера отменял бы фильтры по одному.
    useEffect(() => {
        const params = new URLSearchParams();

        if (appliedQuery) params.set("query", appliedQuery);
        if (appliedBranchId) params.set("branchId", appliedBranchId);
        if (paymentStatus) params.set("paymentStatus", paymentStatus);
        if (page > 1) params.set("page", String(page));

        history.replace({ search: params.toString() });
    }, [history, appliedQuery, appliedBranchId, paymentStatus, page]);

    // Правка фильтра возвращает на первую страницу: иначе выборка из трёх строк открывается
    // на седьмой странице и выглядит пустой.
    const resetPage = () => setPage(1);

    const handleChangeQuery = (value: string) => {
        setQuery(value);
        resetPage();
    };

    const handleChangeBranchId = (value: string) => {
        setBranchId(value.replace(/\D/g, ""));
        resetPage();
    };

    const handleChangePaymentStatus = (value: PaymentStatusDto | null) => {
        setPaymentStatus(value);
        resetPage();
    };

    const handleResetFilters = () => {
        setQuery("");
        setBranchId("");
        setPaymentStatus(null);
        resetPage();
    };

    const buildSignInLink = async (branchId: number) => {
        const token = await createImpersonationToken(branchId);

        return `${WAREHOUSE_URL}?authToken=${token.authToken}`;
    };

    /*
    * Вкладка открывается до запроса токена: Safari на iOS пропускает window.open только прямо по
    * нажатию, а после await молча возвращает null, без ошибки.
    * */
    const handleOpenAsClient = async (branch: OrganizationBranchListDto) => {
        const clientWindow = window.open("", "_blank");

        setBusyBranchId(branch.id);

        try {
            if (!clientWindow) throw new Error("Браузер заблокировал новую вкладку");

            clientWindow.location.href = await buildSignInLink(branch.id);
        } catch (caught) {
            if (clientWindow) clientWindow.close();

            notify.showError("Не удалось войти под клиентом", caught);
        } finally {
            setBusyBranchId(null);
        }
    };

    const handleCopySignInLink = async (branch: OrganizationBranchListDto) => {
        setBusyBranchId(branch.id);

        try {
            await navigator.clipboard.writeText(await buildSignInLink(branch.id));
            notify.showSuccess("Ссылка скопирована");
        } catch (caught) {
            notify.showError("Не удалось скопировать ссылку", caught);
        } finally {
            setBusyBranchId(null);
        }
    };

    return {
        query,
        branchId,
        paymentStatus,
        page,
        items,
        total,
        loading,
        error,
        busyBranchId,
        notify,
        pageSize: PAGE_SIZE,
        hasFilters: query !== "" || branchId !== "" || paymentStatus !== null,
        onChangeQuery: handleChangeQuery,
        onChangeBranchId: handleChangeBranchId,
        onChangePaymentStatus: handleChangePaymentStatus,
        onResetFilters: handleResetFilters,
        onChangePage: setPage,
        onRetry: loadBranches,
        onOpenAsClient: handleOpenAsClient,
        onCopySignInLink: handleCopySignInLink,
    };
};
